import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";

import { getNextEnabledIndex } from "./utils";

type Params = {
    isOpen: boolean;
    disabledFlags: boolean[];
    onSelect: (index: number) => void;
    onClose: () => void;
};

type Result = {
    activeIndex: number;
    listRef: React.RefObject<HTMLDivElement | null>;
    handleKeyDown: (event: React.KeyboardEvent) => void;
    handleItemHover: (index: number) => void;
};

export const useAttendeesSearchKeyboard = ({ isOpen, disabledFlags, onSelect, onClose }: Params): Result => {
    const [activeIndex, setActiveIndex] = useState(-1);
    const listRef = useRef<HTMLDivElement | null>(null);
    const disabledFlagsRef = useRef(disabledFlags);
    disabledFlagsRef.current = disabledFlags;

    // A string key keeps the reset effect from re-running on every render with a fresh array identity.
    const disabledFlagsKey = disabledFlags.join(",");

    useEffect(() => {
        setActiveIndex(isOpen ? getNextEnabledIndex(disabledFlagsRef.current, -1, 1) : -1);
    }, [isOpen, disabledFlagsKey]);

    useEffect(() => {
        if (activeIndex < 0) {
            return;
        }
        listRef.current?.children[activeIndex]?.scrollIntoView({ block: "nearest" });
    }, [activeIndex]);

    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent): void => {
            if (!isOpen) {
                return;
            }

            if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                event.preventDefault();
                const direction = event.key === "ArrowDown" ? 1 : -1;
                setActiveIndex((current) => getNextEnabledIndex(disabledFlagsRef.current, current, direction));
                return;
            }

            if (event.key === "Enter" && activeIndex >= 0 && !disabledFlagsRef.current[activeIndex]) {
                event.preventDefault();
                onSelect(activeIndex);
                return;
            }

            if (event.key === "Escape") {
                event.preventDefault();
                onClose();
            }
        },
        [isOpen, activeIndex, onSelect, onClose],
    );

    const handleItemHover = useCallback((index: number): void => setActiveIndex(index), []);

    return { activeIndex, listRef, handleKeyDown, handleItemHover };
};
